import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    OP_STATUS
} from "../../util/varibles/constants";
import { cloneObj } from "../../util/varibles/global";
import { IOperation, IOperation2, IRoute, IServiceRequest, ISite, IUnit } from "../../util/varibles/interface";
import { initialState, IUpdateOp } from "./constants";
import { IGetActivityLog } from "./saga";

const planOperationSlice = createSlice({
    name: 'planOperation',
    initialState,
    reducers: {
        initialRequest: (state) => {
            state.popupClose = false;
        },
        initialSuccess: (state, action) => {
            const { serviceTasks } = action.payload;
            state.serviceTasks = serviceTasks || [];
        },
        updateWeeks: (state, action) => {
            state.weeks = action.payload;
        },
        confirmOpRequest: (state, action) => {
            state.isFetching = { ...state.isFetching, confirm: true };
        },
        confirmOpSuccess: (state, action) => {
            const { operations, level, operationsDelete, files, fishChanges } = action.payload;
            state.files = files;
            state.operations = { ...operations };
            state.level = level;
            state.operationsDelete = { ...operationsDelete };
            state.isFetching = { ...state.isFetching, confirm: false };
            state.fishChanges = fishChanges;
        },
        confirmOpFailure: (state) => {
            state.isFetching = { ...state.isFetching, confirm: false };
        },
        addExternalSite: (state, action) => {
            const site = action.payload;
            const { sites } = state;
            let isUpdate = false;

            const list = sites.map(item => {
                if (item.id === site.id) {
                    isUpdate = true;
                    return site
                }
                return item;
            });

            state.sites = isUpdate ? list : [...sites, site];
        },
        setFocusId: (state, action) => {
            state.opFocusId = action.payload;
        },
        saveRoutes: (state, action: PayloadAction<IRoute[]>) => {
            const routes = action.payload || [];
            state.routeIds = Array.from(new Set(routes.map(item => [item.source_id, item.destination_id].join('_'))));
        },
        adddRoute: (state, action: PayloadAction<IRoute>) => {
            const route = action.payload;
            if (route)
                state.routeIds = Array.from(new Set([...state.routeIds, [route.source_id, route.destination_id].join('_')]));
        },
        getSitesRequest: (state, action) => {
            state.loadingSites = true;
            state.loadingServiceRequests = true;
        },
        getSitesSuccess: (state, action) => {
            const { sites = [], harvests = [] } = action.payload;
            state.sites = sites;
            state.harvests = harvests.map((item: any) => ({ ...item, id: item.unit_id, unit_id: item.unit_number }));
            state.loadingSites = false;
        },
        getSitesFailure: (state) => {
            state.loadingSites = false;
        },
        setServiceRequests: (state, action) => {
            const { loadingServiceRequests, serviceRequests } = action.payload;

            if (loadingServiceRequests !== undefined) {
                state.loadingServiceRequests = loadingServiceRequests
            }
            if (serviceRequests) {
                state.serviceRequests = serviceRequests
            }
        },
        updateServiceRequests: (state, action: PayloadAction<{ service_requests?: IServiceRequest[], delete_ids?: string[] }>) => {
            const { service_requests = [], delete_ids = [] } = action.payload || {};
            const ids = new Set(delete_ids)

            const mergeOperations = (
                oldOperations: IServiceRequest['operations'] = [],
                newOperations: IServiceRequest['operations'] = []
            ): NonNullable<IServiceRequest['operations']> => {
                const operationMap = new Map<string, NonNullable<IServiceRequest['operations']>[number]>();

                oldOperations.forEach((operation) => {
                    operationMap.set(operation.id, operation);
                });

                newOperations.forEach((operation) => {
                    if (operation.status === OP_STATUS.CANCELED) {
                        operationMap.delete(operation.id);
                        return;
                    }

                    operationMap.set(operation.id, operation);
                });

                return Array.from(operationMap.values());
            };

            const value = cloneObj(ids.size > 0 ? state.serviceRequests.filter(item => !ids.has(item.id)) : state.serviceRequests) as typeof state.serviceRequests;
            service_requests.forEach(item => {
                const index = value.findIndex(sub => sub.id === item.id);
                if (index !== -1) {
                    const operations = mergeOperations(value[index].operations, item.operations)
                    value[index] = { ...value[index], ...item, operations };
                } else {
                    value.push(item);
                }
            })
            state.serviceRequests = value;
        },
        getCancelOps: (state, action) => {
            const { vessels } = state;
            const data = action.payload;
            if (vessels.length > 0) {
                state.canceled = data.map((item: any) => {
                    const vessel = vessels.find((sub: any) => sub.id === item.operation.vessel_id);
                    return { ...item, vessel }
                });
            } else {
                state.canceled = data;
            }
        },
        togglePopupOperation: (_, __) => { },
        renewOp: (_, __: PayloadAction<IOperation>) => { },
        updateSite: (state, action: PayloadAction<ISite>) => {
            const { sites } = state;
            const { id } = action.payload;

            state.sites = sites.map(item => item.id === id ? action.payload : item)
        },
        cancelContract: (state, action) => {
            const [vesselId, contractId] = action.payload;
            const { vessels, operations } = state;
            let operationsNew = { ...operations };
            let index = 0;
            state.vessels = vessels.reduce((list: any, item) => {
                const { id } = item;
                if (id !== vesselId) {
                    list = [...list, { ...item, index }];
                    index++;
                } else {
                    const { contracts } = item;
                    const contractsNew = contracts.filter(contract => contract.id !== contractId);
                    if (contractsNew.length > 0) {
                        index++;
                        return [...list, { ...item, contracts: contractsNew }]
                    } else {
                        operationsNew = Object.keys(operationsNew).reduce((list: any, key) => {
                            return operationsNew[key].vessel.id === vesselId ? list : {
                                ...list,
                                [key]: operationsNew[key]
                            }
                        }, {})
                    }
                }
                return list;
            }, []);
            state.operations = { ...operationsNew };
        },
        clearFocus: (state) => {
            const { opFocusId } = state;
            if (opFocusId)
                state.opFocusId = null
        },
        updateVessels: (state, action) => {
            state.vessels = action.payload
        },
        updateAvailableTime: (state, action) => {
            const available_time = action.payload;
            const { vessel_id, id } = available_time;
            const { vessels } = state;
            state.vessels = vessels.map((item: any) => {
                if (item.id === vessel_id) {
                    let isFlag = true;
                    const available_times = item.available_times.map((sub: any) => {
                        if (sub.id === id) {
                            isFlag = false;
                            return available_time
                        }
                        return sub;
                    });
                    if (isFlag)
                        available_times.push(available_time)
                    return { ...item, available_times }
                }
                return item;
            });
        },
        deleteAvailableTime: (state, action) => {
            const { vessel_id, id } = action.payload;
            const { vessels } = state;
            state.vessels = vessels.map((item: any) => item.id === vessel_id ? {
                ...item,
                available_times: item.available_times.filter((sub: any) => sub.id !== id)
            } : item);
        },
        updateUnitsAndHarvests: (state, action) => {
            const { units = [], harvests = [] } = action.payload;
            state.sites = units.reduce((rs: ISite[], item: any) => {
                const indexSite = rs.findIndex(sub => sub.id === item.site_id);
                if (indexSite !== -1) {
                    const site = rs[indexSite];
                    const { fish_amount, avg_weight, total_weight } = item;
                    rs[indexSite] = {
                        ...site,
                        units: site.units.map(sub => sub.id === item.id
                            ? { ...sub, fish_amount, avg_weight, total_weight }
                            : sub)
                    }
                }
                return rs;
            }, cloneObj(state.sites));
            state.harvests = harvests.reduce((rs: IUnit[], item: any) => {
                const indexHarvest = rs.findIndex(sub => sub.harvest_id === item.harvest_id);
                if (indexHarvest !== -1) {
                    const harvest = rs[indexHarvest];
                    const { fish_amount, avg_weight, total_weight } = item;
                    rs[indexHarvest] = { ...harvest, fish_amount, avg_weight, total_weight }
                }
                return rs;
            }, cloneObj(state.harvests));
        },
        dragOperation: (_, __: PayloadAction<IOperation>) => { },
        updateOps: (_, __: PayloadAction<IUpdateOp>) => { },
        updateOpByWs: (_, __: PayloadAction<{ data: any[], mode?: any }>) => { },
        updateOpsByWs: (_, __: PayloadAction<{ data: any[], mode?: any }>) => { },
        autoPlan: (_, __: PayloadAction<{ operations: IOperation2[], harvests: { [id: string]: any } }>) => { },
        getActivityLog: (_, __: PayloadAction<IGetActivityLog>) => { },
        lazyLoadingRequest: (state, __) => {
            state.loadingLazy = true;
        },
        lazyLoadingSuccess: (state, action) => {
            const { weeks, result, key } = action.payload;
            state.weeks = weeks;
            const { vessels, level, operations } = result;
            state.level = level;
            state.operations = operations;
            state.vessels = vessels;
            (state as any)[key] = result[key];
            state.loadingLazy = false;
        },
        lazyLoadingFailure: (state) => {
            state.loadingLazy = false;
        },
        getVesselsRequest: (state, _) => {
            state.loadingVessels = true
        },
        getVesselsSuccess: (state, action) => {
            const { plan } = action.payload;
            const { before_count: beforeCount, after_count: afterCount } = plan;

            state.level = plan.level;
            state.operations = plan.operations;
            state.vessels = plan.vessels;
            state.beforeCount = beforeCount;
            state.afterCount = afterCount;
            state.loadingVessels = false
        },
        getVesselsFailure: (state) => {
            state.loadingVessels = false
        },
        deleteOps: (_, __: PayloadAction<string[]>) => { },
        clearOps: (_, __: PayloadAction<{ listOfDelete: string[], listOfUpdate: IOperation[] }>) => { },
        mergeOp: (_, __: PayloadAction<{ data: IOperation, deletedId: string }>) => { },
        cancelOp: (_, __: PayloadAction<string>) => { },
        deleteCancelOp: (state, action: PayloadAction<string>) => { },
        closePopup: (state) => {
            state.popupClose = true;
        },
        updateState: (state, action: PayloadAction<Partial<typeof state>>) => {
            Object.keys(action.payload).forEach(key => {
                if (key === "level") {
                    const level = (action.payload as any)[key];
                    state.vessels = state.vessels.map(item => {
                        const { id: vesselId } = item;
                        if (level[vesselId]) {
                            return { ...item, row: Math.max(...Object.keys(level[vesselId]).map(opId => level[vesselId][opId].sub)) }
                        }
                        return item;
                    })
                }
                (state as any)[key] = (action.payload as any)[key];
            })
        }
    },
})

export const planOpActions = planOperationSlice.actions;

const planOperationReducer = planOperationSlice.reducer;

export default planOperationReducer;

